import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { PcDialog } from 'app/shared/dialog/dialog';
import { PageEvent, TablePaginator } from 'app/shared/table-paginator/table-paginator';
import { NotificationService } from 'app/core/services/notification/notification.service';
import { titleCasePersonName } from 'app/shared/utils/person-name';
import { ActivatedRoute, Router } from '@angular/router';
import { PcConfigService } from 'app/shared/services/config';
import { AuthService } from 'app/core/auth/auth.service';
import { InstanceService } from 'app/core/services/instance/instance.service';
import { ModuleService } from 'app/core/services/module/module.service';
import { PreferenceService } from 'app/core/services/preference/preference.service';
import { StorageService } from 'app/core/services/storage/storage.service';
import { TranslationsService } from 'app/core/services/translations/translations.service';
import { MethodCustom } from 'app/shared/utils/method';
import { TitleRouteSeoService } from 'app/shared/utils/title-route-seo.service';
import { GreetingService } from 'app/shared/greeting/greeting.service';
import _, { set } from 'lodash';
import { Subject, Subscription, debounceTime, first, takeUntil, tap } from 'rxjs';
import { Modal } from 'app/shared/molecules/modal/modal';
import { InstanceModal } from 'app/shared/organisms/instance-modal/instance-modal';
import { PopUpButton } from 'app/shared/pop-up-button/pop-up-button';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { Search } from 'app/shared/atoms/search/search';
import { TranslationsComponent } from 'app/shared/organisms/translations/translations';
import { UserComponent } from 'app/layout/common/user/user.component';
import { NativeModalService } from 'app/shared/services/native-modal.service';
import { SvgIcon } from 'app/shared/svg-icon/svg-icon';
import { CommonModule } from '@angular/common';
import { ViewToggle } from 'app/shared/view-toggle/view-toggle';
import { AddButton } from 'app/shared/molecules/add-button/add-button';

/**
 * The in-app path to enter a workspace at, or /home when there is none or it is
 * not safe to follow: only same-origin app paths, never another site
 * ("//host", "https://…") nor the selector or auth pages themselves.
 *
 * @param redirectUrl The redirectURL query param, as AuthGuard sets it
 */
export function safeEntryUrl(redirectUrl: string | null): string {
  if (!redirectUrl || !redirectUrl.startsWith('/') || redirectUrl.startsWith('//')) {
    return '/home';
  }
  if (/^\/(select-instance|auth|sign-out)(\/|\?|$)/.test(redirectUrl)) {
    return '/home';
  }
  return redirectUrl;
}

@Component({
  selector: 'primecontrol-instance-selector',
  standalone: true,
  imports: [
    CommonModule,
    AddButton,
    ViewToggle,
    PopUpButton,
    TablePaginator,
    TranslateModule,
    Search,
    TranslationsComponent,
    UserComponent,
    SvgIcon,
  ],
  templateUrl: './instance-selector.html',
  styleUrls: ['./instance-selector.scss'],
})
export class InstanceSelector implements OnInit, OnDestroy {
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  loading = true;
  public instances: any[];
  public searchInputControl: UntypedFormControl = new UntypedFormControl('');
  public isLoading: boolean = false;
  /** The first fetch has gone out; later ones skip the app-wide splash. */
  private _hasLoadedOnce = false;
  /** The instances fetch in flight, cancelled by the next one. */
  private _listSubscription: Subscription | null = null;
  /** Search term of the latest fetch: the one the rendered list belongs to. */
  private _fetchedTerm = '';
  /**
   * Enter was pressed before the debounced search caught up with the typing:
   * open the first result of the current term as soon as it lands.
   */
  private _openFirstOnLoad = false;
  /** Instance just created from the modal: entered once the reloaded list lands. */
  private _enterOnLoadId: string | null = null;
  public pagination: any;
  public pageIndex = 0;
  public pageSize = 25;
  /**
   * Whether the user may create workspaces: the API marks every instance with
   * Instances.Create for a Super Admin, and only for one. Sticky, so narrowing
   * the list with a search never hides the button.
   */
  public canCreateInstance: WritableSignal<boolean> = signal(false);
  public user: WritableSignal<any> = signal(null);
  public greeting: string;
  public greetingEmoji: string;
  public isNationalGreeting = false;
  public viewMode: WritableSignal<'grid' | 'list'> = signal('grid');
  /**
   * Skeleton placeholders. Sized to the last known instance count (persisted
   * in localStorage) so the vertically-centered layout doesn't re-center when
   * the real cards replace the skeleton. Falls back to 8 on first visit.
   */
  public skeletonItems: number[];
  /**
   * Id of the instance being activated: the chosen card is "picked up" and
   * the rest recede while the token refresh runs. Null when idle.
   */
  public selectingId: WritableSignal<string | null> = signal(null);
  /** Id of the last-used instance, shown first in the grid with a badge. */
  public recentInstanceId: string | null = null;
  /**
   * Last-access timestamp per instance id, tracked locally (per browser)
   * every time a workspace is entered. Feeds the hover metadata on cards.
   */
  private _lastAccessById: Record<string, number> = {};

  /** Card menu rendered by the shared pop-up button (same recipe as tables). */
  public cardMenuOptions = [
    {
      label: 'WORKSPACE_SETTINGS',
      icon: 'settings',
      command: (instance: any) => this.onCardMenuAction('settings', instance),
    },
  ];

  private _matDialog: PcDialog = inject(PcDialog);
  private _modal: NativeModalService = inject(NativeModalService);
  private _greetingService: GreetingService = inject(GreetingService);
  private _notificationService: NotificationService = inject(NotificationService);
  private _translateService: TranslateService = inject(TranslateService);

  /**
   * Where entering a workspace leads: the page the user was headed to when
   * AuthGuard sent them here (a link from an assignment email opens a new tab,
   * which has no instance yet), or /home.
   */
  private readonly _entryUrl: string = safeEntryUrl(
    inject(ActivatedRoute).snapshot.queryParamMap.get('redirectURL')
  );

  constructor(
    private _router: Router,
    private _preferenceService: PreferenceService,
    private _instanceService: InstanceService,
    private _moduleService: ModuleService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _pcConfigService: PcConfigService,
    private _authService: AuthService,
    private _storageService: StorageService,
    private _translationsService: TranslationsService
  ) {
    const user = this._readStoredUser();
    // Every word title-cased ("MARÍA JOSÉ" → "María José"); a missing last name
    // (common on Google/Microsoft sign-ups) renders as nothing instead of throwing.
    this.user.set({
      ...user,
      firstName: titleCasePersonName(user.firstName) ?? '',
      lastName: titleCasePersonName(user.lastName) ?? '',
    });
    const greeting = this._greetingService.resolveGreeting({ trackReturnVisit: false });
    this.greeting = greeting.text;
    this.greetingEmoji = greeting.emoji;
    this.isNationalGreeting = greeting.isNationalDate;

    const lastCount = Number(this._storageService.getItem('primecontrol_instanceCount'));
    const count = Number.isFinite(lastCount) && lastCount >= 1 && lastCount <= 25 ? lastCount : 8;
    this.skeletonItems = Array.from({ length: count }, (_, index) => index);

    try {
      this._lastAccessById =
        JSON.parse(this._storageService.getItem('primecontrol_instanceLastAccess')) ?? {};
    } catch (error) {
      // Corrupt or legacy value: start over rather than break the selector.
      console.error('Could not parse stored instance last-access map:', error);
      this._lastAccessById = {};
    }
  }

  ngOnInit() {
    // Get the pagination
    this._instanceService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: any) => {
        // Update the pagination
        this.pagination = pagination;

        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    this._instanceService.instances$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((instances: any) => {
        // instances$ is a BehaviorSubject seeded with null; ignore that initial
        // emission (the list now loads non-blocking, so we subscribe before the
        // first real value lands).
        if (!instances) return;
        // Always clear the skeleton once a response lands — an empty search
        // result must show the empty state, not skeletons forever.
        this.loading = false;
        if (instances.length > 0) {
          if (!this._fetchedTerm) {
            // Remember the count so the next visit's skeleton matches the grid
            // (a search result is no measure of how many cards there are).
            this._storageService.setItems('primecontrol_instanceCount', instances.length);
          }
          instances = instances.map((item) => {
            // Normalize preference to an object: parse JSON strings, and default
            // null/undefined to {} so the template's instance.preference.* reads
            // don't throw for instances without a preference.
            item.preference =
              typeof item.preference === 'string'
                ? JSON.parse(item.preference)
                : (item.preference ?? {});
            // Localized "2 days ago" shown on card hover; null when this
            // browser has never entered the workspace.
            item._lastAccessLabel = this._formatLastAccess(item.id);
            return item;
          });

          // Most recently entered first; the template marks the first one with a
          // "recent" badge.
          const lastUsedId = this._storageService.getItem('primecontrol_idInstance');
          instances = this._sortByLastAccess(instances, lastUsedId);
          const hasRecent = instances.some((item) => item.id === lastUsedId);
          this.recentInstanceId = hasRecent ? lastUsedId : null;

          if (instances.some((item) => this._holds(item, 'InstancesCreate'))) {
            this.canCreateInstance.set(true);
          }
        }
        // Update the pagination
        this.instances = instances;

        // Mark for check
        this._changeDetectorRef.markForCheck();

        if (this._enterOnLoadId) {
          const createdId = this._enterOnLoadId;
          this._enterOnLoadId = null;
          // Past the first page it is not in this list: enter it with the
          // default branding, which is all a brand-new instance has anyway.
          const created = instances.find((item) => item.id === createdId) ?? {
            id: createdId,
            preference: {},
          };
          this._activateInstance(created, '/home');
          return;
        }
        if (this._isOnlyInstance(instances)) {
          // A user with a single workspace has nothing to choose: enter it,
          // through the same path as a click (token refresh + its branding).
          this._activateInstance(instances[0], this._entryUrl);
          return;
        }
        if (this._openFirstOnLoad) {
          this._openFirstOnLoad = false;
          if (instances.length) {
            this.instanceSelect(instances[0]);
          }
        }
      });

    // The last-access labels are formatted in the UI language: rebuild them
    // when it changes on this page.
    this._translateService.onLangChange
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((event: LangChangeEvent) => {
        if (!this.instances) return;
        this.instances = this.instances.map((item) => ({
          ...item,
          _lastAccessLabel: this._formatLastAccess(item.id, event.lang),
        }));
        this._changeDetectorRef.markForCheck();
      });

    // Load the instance list non-blocking (brandbot pattern): the route no
    // longer blocks on a resolver, so the shell + skeletons render immediately
    // and the cards stream in when this call lands.
    this.getInstance();

    // Subscribe to search input field value changes
    // this.searchInputControl.valueChanges
    // 	.pipe(
    // 		takeUntil(this._unsubscribeAll),
    // 		debounceTime(300),
    // 		tap(() => {
    // 			this.getInstance();
    // 		}),
    // 	)
    // 	.subscribe();
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  ngOnDestroy(): void {
    // instances$ is a root-level subject other pages also feed (the settings
    // resolver reloads it): a subscription left behind would keep reacting —
    // auto-entering a workspace — long after this page is gone.
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this._listSubscription?.unsubscribe();
  }

  /** A new search term starts over from the first page. */
  onSearch(): void {
    this.pageIndex = 0;
    this.getInstance();
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getInstance();
  }

  /** Workspace settings are only offered where the user may edit them. */
  canEditSettings(instance: any): boolean {
    return this._holds(instance, 'InstancesEdit');
  }

  getInstance() {
    this.isLoading = true;
    this._fetchedTerm = (this.searchInputControl.value ?? '').trim();
    // Skeletons only before the first load: while refining a search the
    // current grid stays put until the filtered result lands (no flash).
    if (!this.instances) {
      this.loading = true;
    }
    // The first fetch keeps the app-wide splash (the page is opening); a
    // search refetch runs in the background, and a newer one cancels the one
    // still in flight so a stale result never replaces a newer search.
    const background = this._hasLoadedOnce;
    this._hasLoadedOnce = true;
    this._listSubscription?.unsubscribe();
    this._listSubscription = this._instanceService
      .getInstancesByUser(
        'instanceName',
        'asc',
        this._fetchedTerm,
        this.pageIndex,
        this.pageSize,
        background
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  instanceSelect(instance: any) {
    this._activateInstance(instance, this._entryUrl);
  }

  /**
   * Keyboard flow from the search box: Enter opens the first result of the
   * current filter, ArrowDown hands focus over to the grid.
   */
  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const term = (this.searchInputControl.value ?? '').trim();
      if (term !== this._fetchedTerm || this.isLoading) {
        // The grid still shows the previous term's results (the search waits
        // 300ms after the last keystroke): open the first result of what was
        // actually typed once it lands, never a stale card.
        this._openFirstOnLoad = true;
        if (term !== this._fetchedTerm) {
          this.onSearch();
        }
        return;
      }
      if (!this.loading && this.instances?.length) {
        this.instanceSelect(this.instances[0]);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      document.querySelector<HTMLElement>('[data-instance-card]')?.focus();
    }
  }

  /**
   * Keyboard support for instance cards: Enter/Space activates the instance,
   * arrow keys move focus between cards. Up/Down jump by the rendered column
   * count in the grid view; in the list view (single column) they move by one.
   */
  onCardKeydown(event: KeyboardEvent, instance: any): void {
    // Keys pressed on a control inside the card (its actions menu) belong to
    // that control: Enter there opens the menu, it must not enter the instance.
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.instanceSelect(instance);
      return;
    }
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const card = event.currentTarget as HTMLElement;
    const container = card.parentElement;
    if (!container) return;
    const cards = Array.from(container.querySelectorAll<HTMLElement>('[data-instance-card]'));
    const columns = getComputedStyle(container).gridTemplateColumns.split(' ').length;
    const delta =
      event.key === 'ArrowLeft'
        ? -1
        : event.key === 'ArrowRight'
          ? 1
          : event.key === 'ArrowUp'
            ? -columns
            : columns;
    const targetIndex = cards.indexOf(card) + delta;
    const targetCard = targetIndex >= 0 ? cards[targetIndex] : undefined;
    if (targetCard) {
      targetCard.focus();
      return;
    }
    // Walking up past the first row returns focus to the search box, closing
    // the keyboard loop: search → grid → search.
    if (delta < 0) {
      document.querySelector<HTMLElement>('primecontrol-search input')?.focus();
    }
  }

  /**
   * Fade the remote logo in once it has loaded (see .pc-logo-fade).
   */
  onLogoLoad(event: Event): void {
    (event.target as HTMLElement).classList.add('pc-logo-loaded');
  }

  onCardMenuAction(action: string, instance: any) {
    if (action === 'settings') {
      this._activateInstance(instance, `/settings/instances/${instance.id}/overview`);
    }
  }

  private _activateInstance(instance: any, navigateTo: string) {
    // Ignore further clicks while an activation is already in flight.
    if (this.selectingId()) return;
    this.selectingId.set(instance.id);
    // The token is refreshed BEFORE anything switches: if it fails, the stored
    // instance, theme and preference still match the session the user has.
    this._authService.refreshUserToken(instance.id).subscribe({
      next: () => {
        this._storageService.setItems('primecontrol_idInstance', instance.id);
        const { theme = 'theme-default' } = instance.preference;
        this._pcConfigService.config = { theme };
        this._preferenceService.content = JSON.stringify(instance.preference);
        MethodCustom.preference();
        // Modules are loaded once by NavigationResolver when /home activates —
        // fetching them here too doubled the request on every instance switch.
        // The remote translation catalog is per-instance, so refresh it in the
        // background now that primecontrol_idInstance points at the new one.
        this._translationsService.refreshForInstance();
        this._recordLastAccess(instance.id);
        this._router.navigateByUrl(navigateTo);
      },
      // Release the selection state so the grid is usable again, and say why
      // nothing happened.
      error: () => {
        this.selectingId.set(null);
        this._notificationService
          .notifications({ type: 'error', message: 'INSTANCE_SWITCH_ERROR' })
          .subscribe();
      },
    });
  }

  /** The user's only workspace, on the unfiltered list: there is nothing to pick. */
  private _isOnlyInstance(instances: any[]): boolean {
    return instances.length === 1 && !this._fetchedTerm && this.pagination?.totalItems === 1;
  }

  /** Whether the user holds a workspace permission in this instance (sent by the API). */
  private _holds(instance: any, permission: string): boolean {
    return Array.isArray(instance?.permissions) && instance.permissions.includes(permission);
  }

  /** The signed-in user as stored at sign-in; empty when missing or unreadable. */
  private _readStoredUser(): any {
    try {
      return JSON.parse(this._storageService.getItem('primecontrol_user')) ?? {};
    } catch (error) {
      console.error('Could not parse the stored user:', error);
      return {};
    }
  }

  // openInstance() {
  //   this._matDialog.open(Modal, {
  //     autoFocus: false,
  //     data: {
  //       component: InstanceModal,
  //     },
  //   });
  // }

  /**
   * Creates a workspace and enters it. The list is reloaded first (search
   * cleared) so the new instance is entered with the list's shape — its
   * preference included — exactly as if its card had been clicked.
   */
  openInstance() {
    this._modal
      .open({ component: InstanceModal })
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((created) => {
        if (!created?.id) return;
        this._notificationService
          .notifications({ type: 'success', message: 'INSTANCE_CREATED' })
          .subscribe();
        this._enterOnLoadId = created.id;
        this.searchInputControl.setValue('', { emitEvent: false });
        this.onSearch();
      });
  }

  /**
   * Orders instances from the most recently entered backwards. The last-used
   * instance always leads, even if it predates the last-access map; instances
   * this browser never entered keep the API order after the visited ones
   * (Array.prototype.sort is stable).
   */
  private _sortByLastAccess<T extends { id: string }>(
    instances: T[],
    lastUsedId: string | null
  ): T[] {
    const recencyOf = (item: T): number =>
      item.id === lastUsedId ? Number.POSITIVE_INFINITY : (this._lastAccessById[item.id] ?? 0);
    return [...instances].sort((first, second) => recencyOf(second) - recencyOf(first));
  }

  /** Persist the moment a workspace is actually entered (token refresh OK). */
  private _recordLastAccess(instanceId: string): void {
    this._lastAccessById[instanceId] = Date.now();
    this._storageService.setItems(
      'primecontrol_instanceLastAccess',
      JSON.stringify(this._lastAccessById)
    );
  }

  /**
   * Localized relative time ("hace 2 días" / "2 days ago") since this browser
   * last entered the workspace, or null when it never has. Uses the native
   * Intl.RelativeTimeFormat with the active UI language.
   */
  private _formatLastAccess(
    instanceId: string,
    locale: string = this._translationsService.getCurrentLanguage() || 'es'
  ): string | null {
    const timestamp = this._lastAccessById[instanceId];
    if (!timestamp) return null;
    const elapsedMs = Date.now() - timestamp;
    if (elapsedMs < 0) return null;

    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const MINUTE = 60_000;
    const HOUR = 3_600_000;
    const DAY = 86_400_000;
    if (elapsedMs < HOUR) {
      return formatter.format(-Math.max(1, Math.floor(elapsedMs / MINUTE)), 'minute');
    }
    if (elapsedMs < DAY) {
      return formatter.format(-Math.floor(elapsedMs / HOUR), 'hour');
    }
    const days = Math.floor(elapsedMs / DAY);
    if (days < 30) {
      return formatter.format(-days, 'day');
    }
    if (days < 365) {
      return formatter.format(-Math.floor(days / 30), 'month');
    }
    return formatter.format(-Math.floor(days / 365), 'year');
  }
}
